import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Popup, Toast } from "react-vant";

import { legalDocs, me } from "../api/resources";
import { ApiClientError } from "../api/client";

/**
 * Consent re-prompt gate.
 *
 * Triggers when ALL of the following hold:
 *   1. The user is authenticated (the `/api/me/consent` request succeeded
 *      without 401 — anonymous visitors are not blocked).
 *   2. Either the user has never signed consent (status === null) OR the
 *      stored privacy/terms versions do not match the latest documents.
 *   3. At least one of the latest docs has `requireReconsent: true` — minor
 *      housekeeping bumps without `requireReconsent` do not interrupt the
 *      session.
 *
 * The gate renders its children unconditionally; the sheet is overlaid on
 * top so the app remains usable for anonymous users and remains visible
 * underneath the modal for signed-in users (intentional — PIPL §14 requires
 * informed, voluntary consent, not a forced black screen).
 */
export function ConsentGate({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const privacyQuery = useQuery({
    queryKey: ["legal", "privacy"],
    queryFn: () => legalDocs.privacy(),
    staleTime: 5 * 60_000,
  });
  const termsQuery = useQuery({
    queryKey: ["legal", "terms"],
    queryFn: () => legalDocs.terms(),
    staleTime: 5 * 60_000,
  });
  const consentQuery = useQuery({
    queryKey: ["me", "consent"],
    queryFn: () => me.getConsent(),
    retry: (failureCount, err) => {
      // Don't retry on 401 — anonymous user, no consent required.
      if (err instanceof ApiClientError && err.status === 401) return false;
      return failureCount < 1;
    },
    staleTime: 60_000,
  });

  const saveMutation = useMutation({
    mutationFn: (input: { privacyVersion: string; termsVersion: string }) =>
      me.saveConsent(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me", "consent"] });
      Toast.info("已同意");
    },
    onError: (err) => {
      Toast.fail(err instanceof Error ? err.message : "保存失败");
    },
    onSettled: () => setSubmitting(false),
  });

  const privacy = privacyQuery.data;
  const terms = termsQuery.data;
  const consent = consentQuery.data;

  // If the consent query errored with 401 we treat the user as anonymous and
  // skip the gate entirely. Other errors also bypass to avoid blocking the
  // whole app on a transient outage.
  const consentLoadFailed = consentQuery.isError;

  const needsReconsent =
    !consentLoadFailed &&
    privacy != null &&
    terms != null &&
    (consent === null ||
      (consent !== undefined &&
        ((consent.privacyVersion !== privacy.version && privacy.requireReconsent) ||
          (consent.termsVersion !== terms.version && terms.requireReconsent))));

  const visible = needsReconsent === true;

  const onAgree = () => {
    if (!privacy || !terms) return;
    setSubmitting(true);
    saveMutation.mutate({
      privacyVersion: privacy.version,
      termsVersion: terms.version,
    });
  };

  return (
    <>
      {children}
      <Popup
        visible={visible}
        position="bottom"
        round={false}
        closeable={false}
        style={{
          padding: "24px 20px 28px",
          background: "var(--color-surface)",
          borderTop: "1px solid var(--color-border)",
        }}
      >
        <div style={titleStyle}>请阅读并同意</div>
        <div
          style={{
            width: 24,
            height: 1,
            background: "var(--color-gold)",
            margin: "0 auto 16px",
          }}
        />
        <div style={metaStyle}>
          为了向您提供比价服务，我们需要您理解并同意下列文档。您可点击查看完整内容。
        </div>
        <div style={docListStyle}>
          <DocRow
            label={privacy?.title ?? "隐私政策"}
            version={privacy?.version ?? ""}
            onClick={() => navigate("/legal/privacy")}
          />
          <DocRow
            label={terms?.title ?? "用户协议"}
            version={terms?.version ?? ""}
            onClick={() => navigate("/legal/terms")}
          />
        </div>
        <button
          style={buttonStyle}
          disabled={submitting || !privacy || !terms}
          onClick={onAgree}
        >
          {submitting ? "保存中…" : "我已阅读并同意"}
        </button>
      </Popup>
    </>
  );
}

function DocRow({
  label,
  version,
  onClick,
}: {
  label: string;
  version: string;
  onClick: () => void;
}) {
  return (
    <div style={docRowStyle} onClick={onClick}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ fontSize: 13, color: "var(--color-text)" }}>{label}</span>
        <span
          style={{
            fontSize: 10,
            color: "var(--color-muted)",
            letterSpacing: "0.06em",
          }}
        >
          版本 {version}
        </span>
      </div>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "var(--color-text)",
          letterSpacing: "0.06em",
          borderBottom: "1px solid var(--color-gold)",
          paddingBottom: 1,
        }}
      >
        查看 →
      </span>
    </div>
  );
}

const titleStyle: React.CSSProperties = {
  fontFamily: "var(--font-serif)",
  fontSize: 16,
  fontWeight: 500,
  letterSpacing: "0.01em",
  color: "var(--color-text)",
  marginBottom: 12,
  textAlign: "center",
};
const metaStyle: React.CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-2)",
  lineHeight: 1.7,
  marginBottom: 20,
  textAlign: "center",
  letterSpacing: 0,
};
const docListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 0,
  marginBottom: 20,
  borderTop: "1px solid var(--color-border)",
};
const docRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 0",
  background: "transparent",
  border: "none",
  borderBottom: "1px solid var(--color-border)",
  borderRadius: 0,
  cursor: "pointer",
};
const buttonStyle: React.CSSProperties = {
  width: "100%",
  padding: "14px 16px",
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: "0.06em",
  color: "var(--color-surface)",
  background: "var(--color-text)",
  border: "none",
  borderRadius: 0,
  cursor: "pointer",
};
